import { fmtBRL, fmtDate, fmtTime, STATUS } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "./entregasHelpers";
import { Camera, CheckCircle2, Circle, Coins, MapPin, MessageSquareText, ShieldCheck, StickyNote } from "lucide-react";

const TONES = {
  slate: ["bg-slate-50/60 border-slate-200", "text-slate-800"],
  amber: ["bg-amber-50/60 border-amber-200", "text-amber-800"],
  sky: ["bg-sky-50/60 border-sky-200", "text-sky-800"],
  emerald: ["bg-emerald-50/60 border-emerald-200", "text-emerald-800"],
  orange: ["bg-orange-50/60 border-orange-200", "text-orange-800"],
};

const Bloco = ({ icon: Icon, titulo, children, tone = "slate", testId }) => (
  <div className={`rounded-xl border p-3 ${TONES[tone][0]}`} data-testid={testId}>
    <div className={`text-[11px] font-bold uppercase flex items-center gap-1 mb-1 ${TONES[tone][1]}`}><Icon className="w-3.5 h-3.5" />{titulo}</div>
    {children}
  </div>
);

const Prova = ({ ok, label, testId }) => (
  <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-emerald-700" : "text-slate-400"}`} data-testid={testId} data-ok={!!ok}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}{label}
  </span>
);

/** Detalhe da entrega para conferência (Iter 118): observações do admin e do entregador, troco confirmado, comprovantes. */
export function EntregaDetalheDialog({ entrega: it, onClose, nomes }) {
  if (!it) return null;
  const pedido = it.numero_pedido || it.pedidos_multiplos?.[0] || it.numero_pedido_receita || "—";
  return (
    <Dialog open={!!it} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="entrega-detalhe">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Entrega · pedido {pedido} <StatusBadge s={it.status} /></DialogTitle>
          <DialogDescription>
            {fmtDate(it.data || it.created_at)} · {it.cliente_nome || "Cliente"} · {nomes?.bairro || "—"} · Entregador: {nomes?.entregador || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Bloco icon={StickyNote} titulo="Observação do admin" tone="amber" testId="detalhe-obs-admin">
            <div className="text-sm whitespace-pre-wrap">{it.observacao || <span className="text-slate-400">— nenhuma</span>}</div>
          </Bloco>
          <Bloco icon={MessageSquareText} titulo="Observação do entregador" tone="sky" testId="detalhe-obs-entregador">
            <div className="text-sm whitespace-pre-wrap">{it.observacao_entregador || <span className="text-slate-400">— nenhuma</span>}</div>
          </Bloco>
          <Bloco icon={Coins} titulo="Pagamento e troco" tone={it.troco_confirmado ? "emerald" : "orange"} testId="detalhe-troco">
            <div className="text-sm grid grid-cols-2 gap-x-3 gap-y-0.5">
              <span className="text-muted-foreground">Forma</span><b>{nomes?.forma || "—"}</b>
              <span className="text-muted-foreground">Valor</span><b className="mono">{fmtBRL(it.valor || 0)}</b>
              <span className="text-muted-foreground">Troco para</span><b className="mono">{it.troco_para ? fmtBRL(it.troco_para) : "—"}</b>
              <span className="text-muted-foreground">Troco</span><b className="mono text-orange-700">{it.troco ? fmtBRL(it.troco) : "—"}</b>
              <span className="text-muted-foreground">Valor recebido</span><b className="mono">{it.valor_recebido ? fmtBRL(it.valor_recebido) : "—"}</b>
            </div>
            <div className="mt-2 space-y-1">
              {(it.troco || 0) > 0 && (
                <div data-testid="detalhe-troco-saida" data-ok={!!it.troco_saida_confirmado}>
                  {it.troco_saida_confirmado
                    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800"><CheckCircle2 className="w-4 h-4" />Troco de saída recebido do balcão{it.troco_saida_confirmado_at ? ` às ${fmtTime(it.troco_saida_confirmado_at)}` : ""}{it.troco_saida_valor != null ? ` · ${fmtBRL(it.troco_saida_valor)}` : ""}</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Circle className="w-3.5 h-3.5" />Troco de saída não confirmado no checkout</span>}
                </div>
              )}
              <div data-testid="detalhe-troco-confirmado" data-ok={!!it.troco_confirmado}>
                {it.troco_confirmado
                  ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800"><CheckCircle2 className="w-4 h-4" />Troco entregue ao cliente{it.troco_confirmado_at ? ` às ${fmtTime(it.troco_confirmado_at)}` : ""}{it.troco_confirmado_valor != null ? ` · ${fmtBRL(it.troco_confirmado_valor)}` : ""}</span>
                  : <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Circle className="w-3.5 h-3.5" />Troco ao cliente não confirmado no app</span>}
              </div>
            </div>
          </Bloco>
          <Bloco icon={ShieldCheck} titulo="Comprovantes" tone="slate" testId="detalhe-comprovantes">
            <div className="flex flex-col gap-1">
              <Prova ok={it.comprova_foto || it.foto_evidencia_path} label="Foto de evidência" testId="detalhe-prova-foto" />
              <Prova ok={it.comprova_gps || (it.latitude && it.longitude)} label={`GPS${it.latitude && it.longitude ? ` (${Number(it.latitude).toFixed(5)}, ${Number(it.longitude).toFixed(5)})` : ""}`} testId="detalhe-prova-gps" />
              <Prova ok={it.comprova_codigo} label="Código de 4 dígitos" testId="detalhe-prova-codigo" />
              <div className="text-xs text-muted-foreground mt-1">Saída {it.hr_saida ? fmtTime(it.hr_saida) : "—"} · Chegada {it.hr_chegada ? fmtTime(it.hr_chegada) : "—"}</div>
              {it.motivo && <div className="text-xs"><span className="text-muted-foreground">Motivo:</span> {it.motivo}</div>}
            </div>
          </Bloco>
        </div>
        <div className="flex gap-3 text-[11px] text-muted-foreground"><Camera className="w-3.5 h-3.5" />Fotos e GPS ficam disponíveis pelo ícone da câmera/mapa na tabela. <MapPin className="w-3.5 h-3.5" />Status: {STATUS[it.status] || it.status}</div>
      </DialogContent>
    </Dialog>
  );
}
